/**
 * ChangeOrderPDF.jsx — Clean, professional Change Order PDF
 * Simple typography-driven layout. No flashy shapes.
 * jsPDF only — no html2canvas dependency.
 */
import { jsPDF } from 'jspdf';

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(amount, currency = 'USD') {
  if (amount == null || isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 2,
  }).format(amount);
}

function todayLong() {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function coTotal(co) {
  return co.pricing_type === 'hourly'
    ? (co.hours || 0) * (co.rate || 0)
    : (co.price || 0);
}

function statusLabel(s) {
  return { draft: 'Pending Approval', sent: 'Sent for Review', approved: 'Approved', declined: 'Declined' }[s] || 'Draft';
}

// ── PDF Generator ──────────────────────────────────────────────────────────────
export async function generateChangeOrderPDF({ co, allCOs = [], project, branding }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const PAGE_W = doc.internal.pageSize.getWidth();   // 595.28
  const PAGE_H = doc.internal.pageSize.getHeight();  // 841.89
  const ML = 56;   // left margin
  const MR = 56;   // right margin
  const CW = PAGE_W - ML - MR;   // 483.28
  const FOOTER_H = 45;
  const BOTTOM_LIMIT = PAGE_H - FOOTER_H - 16;

  let y = 0;

  // ── Colour tokens ─────────────────────────────────────────────────────────
  const INK = [18, 18, 22];      // near-black body text
  const SLATE = [60, 60, 72];      // secondary text
  const MUTED = [130, 130, 145];   // captions, labels
  const RULE = [210, 210, 220];   // thin horizontal rules
  const ACCENT = [79, 57, 222];     // purple — used sparingly
  // const WHITE  = [255, 255, 255];

  // ── Draw helpers ──────────────────────────────────────────────────────────
  const setInk = (c) => doc.setTextColor(...c);
  const setDraw = (c) => doc.setDrawColor(...c);
  const setFill = (c) => doc.setFillColor(...c);
  const setLW = (n) => doc.setLineWidth(n);

  function rule(yy, color = RULE, weight = 0.5) {
    setDraw(color); setLW(weight);
    doc.line(ML, yy, PAGE_W - MR, yy);
    return yy + 6;
  }

  function sectionHeader(label, yy) {
    setInk(MUTED);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(label.toUpperCase(), ML, yy);
    return rule(yy + 6, RULE, 0.4) + 2;
  }

  // Wraps and renders text, returns new y
  function textBlock(str, x, yy, maxW, opts = {}) {
    if (!str) return yy;
    const lines = doc.splitTextToSize(String(str), maxW);
    doc.text(lines, x, yy, opts);
    return yy + lines.length * (opts.lineHeight || doc.getFontSize() * 1.4);
  }

  // Two-column label + value row
  function kvRow(label, value, yy, labelW = 130) {
    setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(label, ML, yy);
    setInk(INK); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    const wrapped = doc.splitTextToSize(String(value), CW - labelW);
    doc.text(wrapped, ML + labelW, yy);
    return yy + Math.max(14, wrapped.length * 12);
  }

  // Highlight kv row (for total amount)
  function kvRowHL(label, value, yy, labelW = 130) {
    setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(label, ML, yy);
    setInk(ACCENT); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(String(value), ML + labelW, yy);
    return yy + 16;
  }

  // Page break guard — call before any new section block
  function guardPage(minH) {
    if (y + minH > BOTTOM_LIMIT) {
      // Add footer to current page first
      renderFooter();
      doc.addPage();
      y = 40;
    }
  }

  // Per-page footer
  function renderFooter() {
    const pageCount = doc.internal.pages.length - 1;
    var fy = PAGE_H - FOOTER_H + 10;
    setDraw(RULE); setLW(0.4);
    doc.line(ML, fy - 6, PAGE_W - MR, fy - 6);
    fy += 2;
    setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
    doc.text(`Generated by Fenscope · fenscope.com · ${todayLong()}`, ML, fy);
    const pg = `CO-${String(co.co_number).padStart(3, '0')}  ·  Page ${pageCount}`;
    doc.text(pg, PAGE_W - MR - doc.getTextWidth(pg), fy);
  }

  // ── PAGE 1: HEADER ────────────────────────────────────────────────────────
  // Thin accent top bar
  setFill(ACCENT); setDraw(ACCENT);
  doc.rect(0, 0, PAGE_W, 4, 'F');
  y = 22;

  // ── Logo (if branding) ────────────────────────────────────────────────────
  // Both SVG and raster are rasterised through canvas at 3× DPR before embedding.
  // - Fixes pixelation: jsPDF embeds 3× res bitmap displayed at 1× = crisp.
  // - Fixes format mismatch: all output is real PNG regardless of source format.
  // - No canvas-taint: SVG uses data: URI; raster uses FileReader data URL.
  let logoH = 0;
  const DPR = 3;   // render 3× for crisp output at PDF resolution
  if (branding?.branding_logo_url) {
    try {
      const logoUrl = branding.branding_logo_url;
      const MAX_H = 34, MAX_W = 120;
      const urlPath = logoUrl.split('?')[0].toLowerCase();

      const res = await fetch(logoUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const isSvg = urlPath.endsWith('.svg')
        || (res.headers.get('content-type') || '').includes('svg');

      let img, drawW, drawH;

      if (isSvg) {
        // Parse SVG text to get intrinsic dimensions
        const svgText = await res.text();
        const svgEl = new DOMParser()
          .parseFromString(svgText, 'image/svg+xml')
          .querySelector('svg');
        const vb = (svgEl?.getAttribute('viewBox') || '')
          .trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
        const nW = (vb.length === 4 ? vb[2] : 0)
          || parseFloat(svgEl?.getAttribute('width')  || '0') || 200;
        const nH = (vb.length === 4 ? vb[3] : 0)
          || parseFloat(svgEl?.getAttribute('height') || '0') || 60;
        const s = Math.min(MAX_H / nH, MAX_W / nW, 1);
        drawW = Math.round(nW * s);
        drawH = Math.round(nH * s);

        // data: URI is same-origin — canvas.toDataURL() never throws
        const svgDataUri = `data:image/svg+xml,${encodeURIComponent(svgText)}`;
        img = await new Promise((resolve, reject) => {
          const im = new Image(drawW * DPR, drawH * DPR);   // load at full DPR size
          im.onload  = () => resolve(im);
          im.onerror = reject;
          im.src = svgDataUri;
        });

      } else {
        // Raster (PNG/JPG/WebP): three strategies in order of reliability
        // 1. Direct fetch  — works when the image host sends CORS headers (Supabase Storage, Cloudinary, etc.)
        // 2. Backend proxy  — Node.js has no CORS, works for ANY public URL (requires JWT)
        // 3. crossOrigin img — last resort, works if server sends Allow-Origin header
        const BACKEND   = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const proxyUrl  = `${BACKEND}/api/proxy-image?url=${encodeURIComponent(logoUrl)}`;

        let rawDataUrl = null;

        // Strategy 1: direct fetch (fast, no extra hop)
        if (!rawDataUrl) {
          try {
            const r = await fetch(logoUrl);
            if (r.ok) {
              const blob = await r.blob();
              rawDataUrl = await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onload  = () => resolve(fr.result);
                fr.onerror = reject;
                fr.readAsDataURL(blob);
              });
            }
          } catch { /* CORS blocked — try proxy */ }
        }

        // Strategy 2: backend proxy (bypasses CORS — requires valid JWT)
        if (!rawDataUrl) {
          try {
            // Grab the current Supabase session token (same one the axios interceptor uses)
            const { createClient } = await import('@supabase/supabase-js');
            const sb = createClient(
              process.env.REACT_APP_SUPABASE_URL,
              process.env.REACT_APP_SUPABASE_ANON_KEY,
            );
            const { data: { session } } = await sb.auth.getSession();
            const token = session?.access_token;

            if (token) {
              const r = await fetch(proxyUrl, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (r.ok) {
                const blob = await r.blob();
                rawDataUrl = await new Promise((resolve, reject) => {
                  const fr = new FileReader();
                  fr.onload  = () => resolve(fr.result);
                  fr.onerror = reject;
                  fr.readAsDataURL(blob);
                });
              }
            }
          } catch { /* proxy unavailable or session error */ }
        }

        // Strategy 3: crossOrigin img → canvas (works if server sends Allow-Origin)
        if (!rawDataUrl) {
          rawDataUrl = await new Promise((resolve, reject) => {
            const im = new Image();
            im.crossOrigin = 'anonymous';
            im.onload = () => {
              const c = document.createElement('canvas');
              c.width = im.naturalWidth; c.height = im.naturalHeight;
              c.getContext('2d').drawImage(im, 0, 0);
              try { resolve(c.toDataURL('image/png')); }
              catch { reject(new Error('canvas tainted')); }
            };
            im.onerror = reject;
            im.src = logoUrl;
          });
        }


        img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload  = () => resolve(im);
          im.onerror = reject;
          im.src = rawDataUrl;
        });
        const nW = img.naturalWidth  || 200;
        const nH = img.naturalHeight || 60;
        const s  = Math.min(MAX_H / nH, MAX_W / nW, 1);
        drawW = Math.round(nW * s);
        drawH = Math.round(nH * s);
      }

      // Render at DPR× canvas, display at 1× in jsPDF — crisp, always PNG
      const canvas = document.createElement('canvas');
      canvas.width  = drawW * DPR;
      canvas.height = drawH * DPR;
      const ctx = canvas.getContext('2d');
      ctx.scale(DPR, DPR);
      ctx.drawImage(img, 0, 0, drawW, drawH);

      doc.addImage(canvas.toDataURL('image/png'), 'PNG', ML, y, drawW, drawH);
      logoH = drawH + 6;

    } catch (err) {
      console.warn('[FenscopePDF] Logo skipped:', err.message);
    }
  }


  y += 12;

  // Company name + contact line (left column of header)
  // Text branding always renders; logo is best-effort (may fail CORS on some hosts)
  const companyName = branding?.branding_company_name || branding?.name || '';

  if (companyName) {
    const cy = y + logoH;
    setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text(companyName, ML, cy);
    let dy = cy + 12;
    if (branding?.branding_address) {
      setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
      doc.text(branding.branding_address, ML, dy); dy += 10;
    }
    const contactParts = [branding?.branding_phone, branding?.branding_website].filter(Boolean);
    if (contactParts.length) {
      setInk(MUTED); doc.setFontSize(7.5);
      doc.text(contactParts.join('   ·   '), ML, dy);
    }
  }

  // CO number (right column)
  const coLabel = `CO-${String(co.co_number).padStart(3, '0')}`;
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
  const coW = doc.getTextWidth(coLabel);
  doc.text(coLabel, PAGE_W - MR - coW, y + logoH + 2);

  // Status (below CO number, right)
  setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  const stLabel = statusLabel(co.status || 'draft');
  const stW = doc.getTextWidth(stLabel);
  doc.text(stLabel, PAGE_W - MR - stW, y + logoH + 16);

  y = Math.max(y + logoH + 36, 70);
  y = rule(y, RULE, 0.6);
  y += 10;

  // ── Project meta ──────────────────────────────────────────────────────────
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text('CHANGE ORDER AGREEMENT', ML, y);
  y += 18;

  setInk(SLATE); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  doc.text(`Project:   ${project?.projectName || '—'}`, ML, y); y += 12;
  doc.text(`Issued:    ${todayLong()}`, ML, y); y += 12;
  if (project?.scopeKnowledge?.processedAt) {
    doc.text(
      `Reference: SOW uploaded ${new Date(project.scopeKnowledge.processedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
      ML, y
    ); y += 12;
  }
  y += 10;

  // ── PARTIES ───────────────────────────────────────────────────────────────
  guardPage(80);
  y = sectionHeader('Parties', y);
  y += 10;

  const colMid = ML + CW / 2;

  // Contractor (left)
  setInk(MUTED); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  doc.text('CONTRACTOR', ML, y);
  y += 14;
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text(companyName || '—', ML, y); y += 13;
  setInk(SLATE); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  if (branding?.branding_address) { doc.text(branding.branding_address, ML, y); y += 11; }
  const cp = [branding?.branding_phone, branding?.branding_website].filter(Boolean).join('   ·   ');
  if (cp) { doc.text(cp, ML, y); y += 11; }

  // Client (right) — positioned at same start y as contractor header
  const partiesStartY = y - (cp ? 22 : 11) - 13 - 14;  // rewind to contractor header line
  setInk(MUTED); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  doc.text('CLIENT', colMid, partiesStartY);
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text(co.client_name || '—', colMid, partiesStartY + 11);
  let cly = partiesStartY + 24;
  if (co.client_company) {
    setInk(SLATE); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(co.client_company, colMid, cly); cly += 11;
  }
  if (co.client_email) {
    setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(co.client_email, colMid, cly);
  }

  y += 14;

  // ── DESCRIPTION ───────────────────────────────────────────────────────────
  guardPage(60);
  y = sectionHeader('Description of Change', y);

  y += 10;
  setInk(INK);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  const titleW = doc.splitTextToSize(co.title || '(untitled)', CW);
  doc.text(titleW, ML, y);
  y += titleW.length * 16 + 4;

  if (co.description) {
    setInk(SLATE); doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    y = textBlock(co.description, ML, y, CW, { lineHeight: 13 });
  }
  y += 10;

  // ── FINANCIAL IMPACT ──────────────────────────────────────────────────────
  guardPage(80);
  y = sectionHeader('Financial Impact', y);

  y += 8;

  const currency = co.currency || 'USD';
  const totalAmt = coTotal(co);

  const pricingDesc = co.pricing_type === 'hourly'
    ? `${co.hours || 0} hrs × ${fmt(co.rate, currency)}/hr  (hourly)`
    : 'Fixed fee';

  y = kvRow('Pricing model', pricingDesc, y);
  y = kvRowHL('Change order amount', fmt(totalAmt, currency), y);
  if (co.payment_terms) y = kvRow('Payment terms', co.payment_terms, y);
  if (Number(co.timeline_days) > 0) {
    const tlStr = `+${co.timeline_days} day${co.timeline_days !== 1 ? 's' : ''}${co.new_deadline ? `  (new deadline: ${co.new_deadline})` : ''}`;
    y = kvRow('Schedule impact', tlStr, y);
  }
  y += 10;

  // ── CUMULATIVE CO LOG ─────────────────────────────────────────────────────
  // Only COs with co_number strictly less than current = prior history.
  // CO-001 has no prior entries so no table. CO-003 shows CO-001 and CO-002.
  const prevCOs = allCOs
    .filter(c => (c.co_number || 0) < (co.co_number || 0))
    .sort((a, b) => (a.co_number || 0) - (b.co_number || 0));

  if (prevCOs.length > 0) {
    const allEntries = [...prevCOs, { ...co, _isThis: true }];
    guardPage(allEntries.length * 16 + 60);

    y = sectionHeader('Cumulative Change Order Record', y);

    y += 10;

    // Column headers
    const COL = { num: ML, title: ML + 54, amount: PAGE_W - MR - 90, status: PAGE_W - MR - 44 };
    setInk(MUTED); doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    ['CO #', 'Description', 'Amount', 'Status'].forEach((h, i) => {
      doc.text(h, [COL.num, COL.title, COL.amount, COL.status][i], y);
    });
    y += 4;
    rule(y, RULE, 0.3); y += 15;

    let grandTotal = 0;
    allEntries.forEach((item, idx) => {
      const amt = coTotal(item);
      const isThis = item._isThis;
      grandTotal += amt;

      setInk(isThis ? ACCENT : INK);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(`CO-${String(item.co_number).padStart(3, '0')}`, COL.num, y);

      // Title — truncate to fit
      const titleTxt = doc.splitTextToSize(
        item.title,
        COL.amount - COL.title - 6
      );
      doc.text(titleTxt[0], COL.title, y);

      setInk(isThis ? ACCENT : SLATE); doc.setFontSize(8);
      doc.text(fmt(amt, currency), COL.amount, y);

      setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
      doc.text(statusLabel(item.status || 'draft'), COL.status, y);

      y += 15;
    });

    // Total row
    y += 2; rule(y, RULE, 0.4); y += 12;
    setInk(SLATE); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
    doc.text('Total additional work:', COL.title, y);
    setInk(ACCENT); doc.setFontSize(10);
    doc.text(fmt(grandTotal, currency), COL.amount, y);
    y += 20;
  }

  // ── TERMS ─────────────────────────────────────────────────────────────────
  guardPage(70);
  y = sectionHeader('Terms & Acceptance', y);
  y += 10;

  setInk(SLATE); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  const legalText =
    'This Change Order is issued pursuant to the original Statement of Work / Service Agreement ' +
    'between the parties named above. By signing below, both parties agree that the additional ' +
    'scope, costs, and schedule changes described herein are accepted and authorised. All other ' +
    'terms of the original agreement remain in full force and effect. This document constitutes a ' +
    'binding amendment to that agreement once signed by both parties.';
  y = textBlock(legalText, ML, y, CW, { lineHeight: 12.5 });
  y += 16;

  // ── SIGNATURES ────────────────────────────────────────────────────────────
  guardPage(110);
  y = sectionHeader('Signatures', y);

  const sigColW = (CW - 40) / 2;
  const sigR = ML + sigColW + 40;  // right sig column x

  y += 10;
  // Contractor
  setInk(MUTED); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  doc.text('CONTRACTOR', ML, y);
  y += 8;
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text(companyName || branding?.name || '—', ML, y + 12);
  setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text('Signature:', ML, y + 30);
  setDraw([170, 170, 185]); setLW(0.5);
  doc.line(ML + 48, y + 32, ML + sigColW, y + 32);
  doc.text('Date:', ML, y + 48);
  doc.line(ML + 28, y + 50, ML + sigColW, y + 50);

  // Client
  y -= 8
  setInk(MUTED); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  doc.text('CLIENT', sigR, y);
  y += 8;
  setInk(INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  const clientStr = [co.client_name, co.client_company].filter(Boolean).join(' · ') || '—';
  doc.text(clientStr, sigR, y + 12);
  setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text('Signature:', sigR, y + 30);
  setDraw([170, 170, 185]); setLW(0.5);
  doc.line(sigR + 48, y + 32, sigR + sigColW, y + 32);
  doc.text('Date:', sigR, y + 48);
  doc.line(sigR + 28, y + 50, sigR + sigColW, y + 50);

  y += 64;

  // ── FOOTER ON ALL PAGES ───────────────────────────────────────────────────
  // jsPDF doesn't have a built-in "apply to all pages" — apply to each
  const totalPages = doc.internal.pages.length - 1;
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    var fy = PAGE_H - FOOTER_H + 20;   // line at fy-6, text at fy
    setDraw(RULE); setLW(0.35);
    doc.line(ML, fy - 6, PAGE_W - MR, fy - 6);
    fy += 10;
    setInk(MUTED); doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
    doc.text(`Generated by Fenscope · fenscope.com · ${todayLong()}`, ML, fy);
    const pgStr = `${coLabel}   Page ${pg} of ${totalPages}`;
    doc.text(pgStr, PAGE_W - MR - doc.getTextWidth(pgStr), fy);
  }

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const fileName = `CO-${String(co.co_number).padStart(3, '0')}_${(project?.projectName || 'Project').replace(/[^a-z0-9]/gi, '_')
    }.pdf`;
  doc.save(fileName);
}
